import { createContext, useContext, useEffect, useState } from "react";
import { LANGUAGES, t as _t } from "@/lib/i18n";
import { api } from "@/lib/api";
import { tokenStorage } from "@/lib/tokenStorage";

const I18nCtx = createContext(null);

// Locale-aware helpers — currency, number, date. Falls back to Turkish.
const LOCALE_MAP = { tr: "tr-TR", en: "en-US", ar: "ar-SA", de: "de-DE", ru: "ru-RU" };

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("uc_lang") || "tr");
  const locale = LOCALE_MAP[lang] || "tr-TR";

  const setLang = async (code) => {
    localStorage.setItem("uc_lang", code);
    setLangState(code);
    // Persist to user profile (best-effort, no toast on failure)
    if (tokenStorage.get("uc_token")) {
      try { await api.post("/auth/language", { language: code }); } catch {}
    }
  };

  useEffect(() => {
    const meta = LANGUAGES.find(l => l.code === lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = meta?.rtl ? "rtl" : "ltr";
  }, [lang]);

  // Hydrate language from server on first mount (if user has one saved)
  useEffect(() => {
    if (!tokenStorage.get("uc_token")) return;
    api.get("/auth/me").then((r) => {
      const srv = r.data?.language;
      if (srv && srv !== lang) setLangState(srv);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const t = (key) => _t(key, lang);
  const rtl = LANGUAGES.find(l => l.code === lang)?.rtl || false;

  const fmtCurrency = (n, currency = "TRY") => {
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 2 }).format(Number(n) || 0);
    } catch { return `₺${Number(n || 0).toFixed(2)}`; }
  };
  const fmtNumber = (n, digits = 2) => {
    try {
      return new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(Number(n) || 0);
    } catch { return String(n); }
  };
  const fmtDate = (iso, opts = { dateStyle: "medium" }) => {
    if (!iso) return "";
    try { return new Intl.DateTimeFormat(locale, opts).format(new Date(iso)); }
    catch { return String(iso).slice(0, 10); }
  };
  const fmtDateTime = (iso) => fmtDate(iso, { dateStyle: "medium", timeStyle: "short" });

  return (
    <I18nCtx.Provider value={{ lang, locale, setLang, t, rtl, languages: LANGUAGES, fmtCurrency, fmtNumber, fmtDate, fmtDateTime }}>
      {children}
    </I18nCtx.Provider>
  );
}

export const useI18n = () => useContext(I18nCtx);
